/**
 * GuildDataSpaceCard — the owner's control for the guild's shared Vault data
 * space (Settings tab of Guild Management).
 *
 * Enabling is owner-only, opt-in, and one-way (V1 has no disable — data
 * members can already see must not vanish on a toggle), so the action sits
 * behind a confirm dialog that spells out exactly what turning it on grants:
 * every member can view shared documents and notes; moderators and admins
 * (plus custom roles granted "Manage shared data") can add and edit them.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/use-toast';
import { FolderOpen, Users } from 'lucide-react';
import { api, type GuildDetailResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function GuildDataSpaceCard({ guild }: { guild: GuildDetailResponse }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const isOwner = guild.myRole === 'owner';
  const holderId = guild.holderId ?? null;

  const enable = useMutation({
    mutationFn: () => api.enableGuildDataSpace(guild.guildId),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.guilds.detail(guild.guildId) }),
        // The new space appears in every HolderSwitcher instance.
        qc.invalidateQueries({ queryKey: queryKeys.vault.me.memberships() }),
      ]);
      setConfirming(false);
      toast({
        title: 'Shared data space enabled',
        description: 'Members can now find it in the workspace switcher and My Data.',
      });
    },
    onError: (err) => {
      toast({
        title: 'Could not enable shared data',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="text-base flex items-center gap-2">
            <FolderOpen className="h-4 w-4" aria-hidden="true" />
            Shared data
          </CardTitle>
          <CardDescription>
            A shared space for documents and notes that every guild member can see.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {holderId ? (
          <>
            <p className="text-sm text-muted-foreground">
              <Users className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
              Enabled — members can view shared documents and notes. Moderators and admins (and
              custom roles granted &ldquo;Manage shared data&rdquo;) can add and edit them.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to={`/my-data?holder=${encodeURIComponent(holderId)}`}>Open in My Data</Link>
            </Button>
          </>
        ) : isOwner ? (
          <>
            <p className="text-sm text-muted-foreground">
              Not enabled. Turning this on gives every current and future member access to the
              guild&rsquo;s shared documents and notes
              {guild.visibility === 'public'
                ? ' — including anyone who joins this public guild'
                : ''}
              . This cannot be turned off later.
            </p>
            <Button type="button" size="sm" onClick={() => setConfirming(true)}>
              Enable shared data space
            </Button>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            Not enabled. Only the guild owner can enable the shared data space.
          </p>
        )}
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable shared data for “{guild.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Every member — current and future — will be able to see documents and notes placed in
              the shared space. Moderators and admins can add and edit content; you can grant that
              to other members with a custom role holding “Manage shared data”.
              {guild.visibility === 'public' && (
                <> This guild is public: anyone who joins gains view access the moment they join.</>
              )}{' '}
              This cannot be turned off later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enable.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={enable.isPending}
              onClick={(e) => {
                e.preventDefault();
                enable.mutate();
              }}
            >
              {enable.isPending ? 'Enabling…' : 'Enable shared data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
